import { Postcode } from '@dreryk/address-common-api';
import { Guardian, GuardianRelation } from '@dreryk/egabinet-patient-frontend-api';
import { Pesel } from '@dreryk/personal-external-identifier-common-api';

import { commonTranslationKeys, FormContextValues, useFormController } from '@dreryk/react-common';
import { AutoLayout, Heading, ModalCancelButton, ModalCloseButton, TextView } from '@dreryk/react-components';

import { EntitlementStatementSection } from '../../../common/EntitlementStatementForm/sections/EntitlementStatementSection';
import { entitlementStatementsTranslationKeys } from '../../../translations';
import { RepresentativeModalConfirmationButton } from '../modal/RepresentativeModalConfirmationButton';

import { EntitlementStatementIdentityDocumentFormPart } from './EntitlementStatementIdentityDocumentFormSection';
import { EntitlementStatementRepresentativeFormBlock } from './EntitlementStatementRepresentativeFormBlock';
import {
   EntitlementStatementPatientDataFormBlock,
   getInitialValues,
   PatientEntitlementStatementCreateFormBean,
   PatientEntitlementStatementCreateFormContentProps,
} from './PatientEntitlementStatementCreateForm';
import { RepresentativeEntitlementStatementCreateFormLoader } from './RepresentativeEntitlementStatementCreateFormLoader';

export type RepresentativeEntitlementStatementCreateFormBean = PatientEntitlementStatementCreateFormBean &
   RepresentativeEntitlementStatementFormPart & {
      existingRepresentative?: Guardian | null;
   };

export type RepresentativeEntitlementStatementFormPart = {
   firstName: string;
   lastName: string;
   guardianRelation: GuardianRelation;
   pesel?: Pesel;
   representativeStreet: string;
   representativeHouse: string;
   representativeFlat: string | null;
   representativeTown: string | null;
   representativePostcode: Postcode | null;
   representativeIdentityDocument: EntitlementStatementIdentityDocumentFormPart;
};

export type RepresentativeEntitlementStatementCreateFormController = FormContextValues<RepresentativeEntitlementStatementCreateFormBean>;

type RepresentativeEntitlementStatementCreateFormContentProps = RepresentativeEntitlementStatementCreateFormProps & {
   readonly guardians: Guardian[];
};

export const RepresentativeEntitlementStatementCreateFormContent = (
   props: RepresentativeEntitlementStatementCreateFormContentProps
): JSX.Element => {
   const {
      register,
      formState,
      setValue,
      getValue,
      watch,
      handleSubmit,
      trigger,
      reset,
   } = useFormController<RepresentativeEntitlementStatementCreateFormBean>({
      ...getInitialValues(props.patient, props.legalBasis),
      guardianRelation: 'MOTHER',
      existingRepresentative: props.guardians.at(0),
   });

   const { effectiveDate, submissionDate } = watch(['effectiveDate', 'submissionDate']);

   return (
      <>
         <AutoLayout as={'form'} noValidate $direction={'vertical'} $horizontalResizing={750} $spacing={24}>
            <EntitlementStatementSection register={register} legalBasis={props.legalBasis} />
            <EntitlementStatementRepresentativeFormBlock
               guardians={props.guardians}
               setValue={setValue}
               getValue={getValue}
               register={register}
               watch={watch}
               reset={reset}
            />
            <AutoLayout as={'div'} $direction={'vertical'}>
               <Heading
                  testId={'patientNameHeading'}
                  type={'h2'}
                  content={entitlementStatementsTranslationKeys.createModal.patientData}
               />
               <TextView
                  label={entitlementStatementsTranslationKeys.createModal.firstNameAndLastName}
                  testId={'patientFirstNameAndLastName'}
               >
                  {`${props.patient?.firstName} ${props.patient?.lastName}`}
               </TextView>
            </AutoLayout>
            <EntitlementStatementPatientDataFormBlock setValue={setValue} watch={watch} register={register} />
         </AutoLayout>
         <RepresentativeModalConfirmationButton
            handleSubmit={handleSubmit}
            trigger={trigger}
            patientId={props.patientId}
            differentDates={submissionDate && effectiveDate ? !effectiveDate.isEqual(submissionDate) : true}
            onDataChanged={props.onDataChanged}
            setEntitlementStatementId={props.setEntitlementStatementId}
         />
         <ModalCancelButton
            label={commonTranslationKeys.cancel}
            onClick={props.onCancel}
            shouldConfirmExit={formState.isDirty}
         />
         <ModalCloseButton onClick={props.onCancel} shouldConfirmExit={formState.isDirty} />
      </>
   );
};

type RepresentativeEntitlementStatementCreateFormProps = PatientEntitlementStatementCreateFormContentProps;

export const RepresentativeEntitlementStatementCreateForm = (
   props: RepresentativeEntitlementStatementCreateFormProps
): JSX.Element => {
   return (
      <RepresentativeEntitlementStatementCreateFormLoader patientId={props.patientId}>
         {(responseProps) => <RepresentativeEntitlementStatementCreateFormContent {...props} {...responseProps} />}
      </RepresentativeEntitlementStatementCreateFormLoader>
   );
};
